import React, { Suspense, memo } from "react"
import { Search } from "./Search"
import { useFuzzyCommandData } from "./useFuzzySearchResults"
import {
  useAttachGlobalSearchKeyboardShortcut,
  useSearchActions,
} from "./useSearch"

const LazyModal = React.lazy(() =>
  import("@tokenterminal/core/ui/overlays/modal/Modal").then((mod) => ({
    default: mod.Modal,
  }))
)

type SearchProviderProps = {
  /* Helpful for disabling the global search overlay when there's already a
  search element on the page that we want to focus. e.g. the Home page */
  disableGlobalOverlay?: boolean
}

/**
 * A global provider that renders the search modal and attaches the global
 * search keyboard shortcut.
 */

export const SearchProvider = memo(function SearchProvider({
  disableGlobalOverlay,
}: SearchProviderProps) {
  const { closeSearch, isOpen, toggleSearch } = useSearchActions()
  useAttachGlobalSearchKeyboardShortcut(toggleSearch)

  // Pre-fetch the data needed to generate fuzzy search results
  useFuzzyCommandData()

  if (disableGlobalOverlay) return null

  return (
    <Suspense fallback={null}>
      <LazyModal
        onOpenChange={closeSearch}
        overlayClassName="!items-start pt-6 md:pt-[100px] z-[999999]"
        isDismissable={true}
        size="auto"
        isOpen={isOpen}
        animationExitDuration={0}
        aria-label="Search"
      >
        <Search closeSearch={closeSearch} />
      </LazyModal>
    </Suspense>
  )
})
